"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import type { QuizTemplate } from "@/lib/personal-quiz/templates";
import { effectiveDraft, recommendedDraft, type QuizDraft } from "./quiz-draft";

export function useQuizTemplates(setDraft: (draft: QuizDraft) => void) {
  const [items, setItems] = useState<QuizTemplate[]>([]);
  const [selected, setSelected] = useState("recommended");
  // 목록 조회와 독립적으로, 실제로 편집을 시작한 버전을 보관한다.
  const [editing, setEditing] = useState<QuizTemplate | null>(null);
  const stale = !!editing && items.some((item) => item.id === editing.id && item.version !== editing.version);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const alive = useRef(false);
  const pending = useRef<{ key: string; id: string } | null>(null);
  const inFlight = useRef(false);
  const readVersion = useRef(0);
  const load = useCallback(async () => {
    const version = ++readVersion.current;
    try {
      const response = await fetch("/api/personal-quiz/templates");
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      if (alive.current && version === readVersion.current) setItems(data.templates);
    } catch (e) {
      if (alive.current && version === readVersion.current) {
        setError(e instanceof TypeError
          ? "저장한 시험지 목록을 불러오지 못했어요. 인터넷 연결을 확인한 뒤 목록을 다시 불러와 주세요."
          : e instanceof Error ? e.message : "저장한 시험지를 불러오지 못했어요.");
      }
    } finally {
      if (alive.current && version === readVersion.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    alive.current = true;
    // load updates React state only after the network request settles.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
    return () => { alive.current = false; };
  }, [load]);
  const reload = () => {
    setLoading(true);
    setError(null);
    return load();
  };
  const select = (id: string) => {
    if (inFlight.current || loading) return;
    const item = items.find((entry) => entry.id === id);
    if (id !== "recommended" && !item) return;
    setSelected(id);
    setEditing(item ?? null);
    setDraft(item ? structuredClone(item.conditions) : recommendedDraft());
    setError(null);
    setMessage(null);
  };
  const save = async (name: string, draft: QuizDraft, overwrite: boolean) => {
    if (inFlight.current || loading) return false;
    const item = editing;
    if (overwrite && (!item || stale)) return false;
    const conditions = effectiveDraft(draft);
    const key = JSON.stringify({ name, conditions, overwrite, id: item?.id, version: item?.version });
    if (pending.current?.key !== key) pending.current = { key, id: crypto.randomUUID() };
    inFlight.current = true;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const response = await fetch("/api/personal-quiz/templates", {
        method: overwrite ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: overwrite ? item!.id : pending.current.id, name, conditions, version: overwrite ? item!.version : 0 }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      if (!alive.current) return false;
      const saved: QuizTemplate = data.template;
      readVersion.current++;
      setItems((old) => [...old.filter((entry) => entry.id !== saved.id), saved].sort((a, b) => a.name.localeCompare(b.name)));
      setSelected(saved.id);
      setEditing(saved);
      setMessage("시험지를 저장했어요.");
      pending.current = null;
      return true;
    } catch (e) {
      if (alive.current) {
        setError(e instanceof TypeError
          ? "시험지 저장 결과를 확인하지 못했어요. 입력한 이름과 조건은 그대로 있으니 인터넷 연결을 확인한 뒤 다시 저장해 주세요."
          : e instanceof Error ? e.message : "저장하지 못했어요. 다시 시도해 주세요.");
      }
      return false;
    } finally {
      inFlight.current = false;
      if (alive.current) setSaving(false);
    }
  };
  return { items, selected, stale, loading, saving, error, message, reload, select, save };
}
export type QuizTemplatesState = ReturnType<typeof useQuizTemplates>;
